import React, { useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { useOrganizationUpdaterProvider } from '../../../context_providers/organization_context/OrganizationProvider';
import { setRequireJoinApproval } from '../../../repository/OrganizationDAO';
import { StringKeys } from '../../../resources/strings/StringKeys';
import COLORS from '../../../resources/style/Themes';
import EditOrganizationViaAdminModal from './modals/EditOrganizationViaAdminModal';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import { useToast } from '../../../context_providers/toast_context/ToastContext';
import { FancySwitchComponent } from '@/components/form_elements/FancySwitch';
import {
  buildPoolCounts,
  getPoolPurchased,
} from '@/utils/organization/orgSeatAvailability';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import { getIsOrgOnTrial } from '@/utils/organization/getIsOrgOnTrial';
import { SubscriptionTier } from '@/../generated-client/models';

type DetailRowProps = {
  label: string;
  value: React.ReactNode;
  labelColor?: string;
  isLast?: boolean;
};

const DetailRow = ({ label, value, labelColor }: DetailRowProps) => (
  <Box sx={{ py: 2 }}>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 500,
        color: labelColor || COLORS.GREY,
        mb: 1,
      }}
    >
      {label}
    </Typography>

    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 400,
          color: COLORS.SAFEGUARD_BLUE,
          wordBreak: 'break-word',
        }}
      >
        {value || '-'}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

const OrganizationDetails = () => {
  const { organization, updateOrganization } = useOrganizationUpdaterProvider();
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const requireJoinApprovalFromContext = !!organization?.require_join_approval;

  const [showEditOrganizationModal, setShowEditOrganizationModal] =
    useState(false);
  const [showPin, setShowPin] = useState(false);
  const [isUpdatingApproval, setIsUpdatingApproval] = useState(false);
  const [showApprovalConfirmModal, setShowApprovalConfirmModal] =
    useState(false);
  const [pendingApprovalValue, setPendingApprovalValue] = useState<
    boolean | null
  >(null);
  const [requireJoinApprovalValue, setRequireJoinApprovalValue] = useState(
    requireJoinApprovalFromContext,
  );

  // When not saving, always reflect the real org state. While saving, freeze to local state.
  const displayedRequireJoinApprovalValue = isUpdatingApproval
    ? requireJoinApprovalValue
    : requireJoinApprovalFromContext;

  const handleRequireJoinApprovalChange = async (checked: boolean) => {
    if (!organization || isUpdatingApproval) return;

    // Freeze current displayed state while the API call is in flight (spinner shows).
    setRequireJoinApprovalValue(requireJoinApprovalFromContext);
    setIsUpdatingApproval(true);
    try {
      await setRequireJoinApproval(checked);
      setRequireJoinApprovalValue(checked);
      updateOrganization({
        ...organization,
        require_join_approval: checked,
      });
      showSuccessToast(
        checked
          ? t(StringKeys.APPROVAL_REQUIREMENT_ENABLED)
          : t(StringKeys.APPROVAL_REQUIREMENT_DISABLED),
      );
    } catch (error) {
      console.error('Failed to update require join approval setting:', error);
      showErrorToast(t(StringKeys.APPROVAL_REQUIREMENT_REQUEST_FAILED));
    } finally {
      setIsUpdatingApproval(false);
    }
  };

  const handleApprovalSwitchChange = (checked: boolean) => {
    if (!organization || isUpdatingApproval) return;

    const isCurrentlyOn = requireJoinApprovalFromContext;

    if (!isCurrentlyOn && checked) {
      setPendingApprovalValue(true);
      setShowApprovalConfirmModal(true);
      return;
    }

    if (isCurrentlyOn && !checked) {
      setPendingApprovalValue(false);
      setShowApprovalConfirmModal(true);
      return;
    }

    handleRequireJoinApprovalChange(checked);
  };

  const handleConfirmApprovalChange = async () => {
    setShowApprovalConfirmModal(false);

    if (pendingApprovalValue !== null) {
      await handleRequireJoinApprovalChange(pendingApprovalValue);
      setPendingApprovalValue(null);
    }
  };

  if (!organization) {
    return null;
  }

  const orgPoolCounts = buildPoolCounts(organization);
  const opsPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Ops);
  const respondPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Respond);
  const opsLabel = getTierLabelKey(SubscriptionTier.Ops);
  const respondLabel = getTierLabelKey(SubscriptionTier.Respond);

  const isOnFreeTrial = getIsOrgOnTrial(organization.trial_end_timestamp);
  const trialEndDateLabel = organization.trial_end_timestamp
    ? new Date(organization.trial_end_timestamp).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const leftColumnRows = [
    {
      label: t(StringKeys.ADDRESS),
      value: organization.address || '-',
    },
    {
      label: t(StringKeys.PRIMARY_CONTACT),
      value: organization.primary_contact || '-',
    },
    {
      label: t(StringKeys.PHONE_NUMBER),
      value: organization.phone_number || '-',
    },
    {
      label: t(StringKeys.EMAIL),
      value: organization.email || '-',
    },
  ];

  const rightColumnRows = [
    {
      label: t(StringKeys.ORGANIZATION_CODE),
      value: organization.org_code || '-',
    },
    {
      label: t(StringKeys.ORGANIZATION_PIN),
      value:
        organization.join_org_pin === null ? (
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 400,
              color: COLORS.TEXT_HEADER_GRAY,
            }}
          >
            {t(StringKeys.DISABLED)}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 400,
                color: COLORS.TEXT_HEADER_GRAY,
              }}
            >
              {showPin ? organization.join_org_pin : '••••••••'}
            </Typography>

            <IconButton
              onClick={() => setShowPin(!showPin)}
              sx={{ p: 0, color: COLORS.GREY }}
            >
              {showPin ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Box>
        ),
    },
    {
      label: t(StringKeys.ORGANIZATION_TOTAL_SUBSCRIPTIONS).replace(
        /:\s*$/,
        '',
      ),
      value: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: 16, fontWeight: 400, color: COLORS.SAFEGUARD_BLUE }}
          >
            {opsLabel}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 2,
              backgroundColor: COLORS.BUTTON_BACKGROUND_BLUE,
              minWidth: 32,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: COLORS.SAFEGUARD_BLUE,
              }}
            >
              {opsPurchased}
            </Typography>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: COLORS.LIGHT_GRAY,
              mx: 1,
              borderWidth: '1px',
            }}
          />

          <Typography
            component="span"
            sx={{ fontSize: 16, fontWeight: 400, color: COLORS.SAFEGUARD_BLUE }}
          >
            {respondLabel}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 2,
              backgroundColor: COLORS.BUTTON_BACKGROUND_GREEN,
              minWidth: 32,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: COLORS.DARK_GREEN,
              }}
            >
              {respondPurchased}
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <>
      {/* Outer wrapper Card - transparent container for the entire component */}
      <Card
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          maxWidth: 900,
          mx: { xs: 0, lg: 0 },
        }}
      >
        {/* Header section - displays organization name */}
        <Box
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: COLORS.WHITE,
              textTransform: 'uppercase',
              lineHeight: 1.4,
            }}
          >
            {organization.company_name}
          </Typography>
        </Box>

        {/* Main content area */}

        <Box sx={{ py: 2, backgroundColor: 'transparent' }}>
          {/* Two column Grid Layout  */}

          <Grid container spacing={2} alignItems="stretch">
            {/* Left column - org contact and address card*/}

            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
              <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
                <Card
                  elevation={0}
                  sx={{
                    backgroundColor: COLORS.TABLE_ROW_GRAY,
                    boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowEditOrganizationModal(true)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: COLORS.TEXT_HEADER_GRAY,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        color: COLORS.BLUE_CRAYOLA,
                        transform: 'translateY(-2px)',
                        backgroundColor: COLORS.TABLE_HEADER_GRAY,
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {leftColumnRows.map((row, index) => (
                    <React.Fragment key={row.label}>
                      <DetailRow
                        label={row.label}
                        value={row.value}
                        isLast={index === leftColumnRows.length - 1}
                      />
                      {index < leftColumnRows.length - 1 && (
                        <Divider
                          sx={{
                            borderColor: COLORS.WHITE,
                            borderBottomWidth: '2px',
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Card>
              </Box>
            </Grid>

            {/* Right column */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
              <Stack
                spacing={2}
                sx={{
                  flex: 1,
                }}
              >
                {/* Organization Code, PIN, and Subscription Count card */}

                <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: COLORS.TABLE_ROW_GRAY,
                      boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setShowEditOrganizationModal(true)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: COLORS.TEXT_HEADER_GRAY,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          color: COLORS.BLUE_CRAYOLA,
                          transform: 'translateY(-2px)',
                          backgroundColor: COLORS.TABLE_HEADER_GRAY,
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {rightColumnRows.map((row, index) => (
                      <React.Fragment key={row.label}>
                        <DetailRow label={row.label} value={row.value} />
                        {index < rightColumnRows.length - 1 && (
                          <Divider
                            sx={{
                              borderColor: COLORS.WHITE,
                              borderBottomWidth: '2px',
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Card>
                </Box>

                {/* Require Join Approval Card */}

                <Box>
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: COLORS.TABLE_ROW_GRAY,
                      boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      flex: 0,
                    }}
                  >
                    <DetailRow
                      label={t(
                        StringKeys.REQUIRE_APPROVAL_TO_JOIN_ORGANIZATION,
                      )}
                      labelColor={COLORS.SAFEGUARD_BLUE}
                      value={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 400,
                              color: displayedRequireJoinApprovalValue
                                ? COLORS.SAFEGUARD_BLUE
                                : COLORS.TEXT_HEADER_GRAY,
                            }}
                          >
                            {displayedRequireJoinApprovalValue
                              ? t(StringKeys.YES)
                              : t(StringKeys.NO)}
                          </Typography>

                          <FancySwitchComponent
                            checked={displayedRequireJoinApprovalValue}
                            disabled={isUpdatingApproval}
                            onChange={(event) => {
                              handleApprovalSwitchChange(event.target.checked);
                            }}
                            sx={{
                              ml: 0.5,
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                {
                                  backgroundColor: COLORS.BLUE_CRAYOLA,
                                },
                            }}
                          />

                          {isUpdatingApproval && (
                            <CircularProgress
                              size={16}
                              sx={{ color: COLORS.BLUE_CRAYOLA, ml: 0.5 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </Card>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          {isOnFreeTrial && trialEndDateLabel && (
            <Box sx={{ mt: 2 }}>
              <Card
                elevation={0}
                sx={{
                  backgroundColor: '#fff5e6',
                  border: '1px solid #fec461ff',
                  borderRadius: 2,
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                  px: 3,
                  py: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InfoOutlinedIcon
                    sx={{ color: COLORS.SAFEGUARD_BLUE, fontSize: 18 }}
                  />
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 500,
                      color: COLORS.SAFEGUARD_BLUE,
                    }}
                  >
                    {t(StringKeys.ORGANIZATION_ON_FREE_TRIAL_TITLE)}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: COLORS.TEXT_HEADER_GRAY,
                    pl: 3.75,
                  }}
                >
                  {t(StringKeys.ORGANIZATION_ON_FREE_TRIAL_DESCRIPTION, {
                    trialEndDate: trialEndDateLabel,
                  })}
                </Typography>
              </Card>
            </Box>
          )}
        </Box>
      </Card>

      <EditOrganizationViaAdminModal
        show={showEditOrganizationModal}
        onHide={() => setShowEditOrganizationModal(false)}
      />

      <ConfirmModal
        show={showApprovalConfirmModal}
        showCancelButton={true}
        onHide={() => {
          setShowApprovalConfirmModal(false);
          setPendingApprovalValue(null);
        }}
        confirmTitle={
          pendingApprovalValue
            ? t(StringKeys.ENABLE_JOIN_APPROVAL_REQUIREMENT)
            : t(StringKeys.DISABLE_JOIN_APPROVAL_REQUIREMENT)
        }
        confirmMessage={
          pendingApprovalValue
            ? t(StringKeys.APPROVAL_ENABLE_DISCLAIMER, {
                organizationName: organization.company_name,
              })
            : t(StringKeys.APPROVAL_DISABLE_DISCLAIMER, {
                organizationName: organization.company_name,
              })
        }
        confirm={handleConfirmApprovalChange}
      />
    </>
  );
};

export default OrganizationDetails;
